"use client"

import {
  memo,
  useCallback,
  useState,
} from "react"

import {
  useMusicContext,
} from "@/context/musicContext"

import {
  Heart,
  CircleArrowDown,
  MoreHorizontal,
} from "lucide-react"

import TrackContextMenu from "./TrackContextMenu"
import usePlaylistTrackState from "../hooks/usePlaylistTrackState"

import TextBoldL from "@/components/text/text-bold-l"
import TextRegularM from "@/components/text/text-regular-m"

import Playgif from "@/image/now-play.webp"
import * as Icons from "@/components/icons"

/* ================================================================
   TYPES
================================================================ */

type Song = {
  index: string
  songName: string
  songArtist: string
  songimg: string
  link: string
  trackTime: string
}

type playlistTracklistProps = {
  data: {
    listType: string
    song: Song
    playlist?: any
  }
}

/* ================================================================
   SHARED GRID
   Header + Track usan exactamente la misma estructura.
================================================================ */

const TRACK_GRID =
  "grid-cols-[1.5rem_3rem_minmax(0,1fr)_2rem_2rem_2rem_3.5rem]"

const TRACK_GRID_MOBILE =
  "grid-cols-[1.25rem_2.75rem_minmax(0,1fr)_1.75rem_1.75rem_1.75rem_3rem]"

/* ================================================================
   TRACK HEADER

   Se mantiene en ESTE MISMO ARCHIVO para que la arquitectura
   visual de playlistTracklist quede centralizada.

   Importante:
   NO debe renderizarse dentro de cada playlistTracklist.
   PlaylistPage debe renderizarlo una sola vez antes del map.
================================================================ */

export function PlaylistTrackHeader() {
  return (
    <div
      role="row"
      className={`
        sticky
        top-0
        z-30
        mb-0 mt-4
        grid
        ${TRACK_GRID_MOBILE}
        items-center
        gap-2
        rounded-lg
        //border
        //border-white/[.04]
        //bg-[var(--soft-bg)]
        px-2
        py-0
        text-white/35
        backdrop-blur-md
        lg:${TRACK_GRID}
        lg:gap-3
        lg:px-4
      `}
    >
      {/* ========================================================
          INDEX
      ======================================================== */}

      <div
        role="columnheader"
        className="
          flex
          min-w-0
          items-center
          justify-center
        "
      >
        <span
          className="
            text-[.62rem]
            font-medium
            uppercase
            tracking-[.08em]
          "
        >
          #
        </span>
      </div>

      {/* ========================================================
          COVER SPACER

          No ponemos ningún texto aquí.
          Esta columna existe para mantener la simetría exacta
          con las filas.
      ======================================================== */}

      <div
        aria-hidden="true"
        className="
          h-8
          w-12
        "
      />

      {/* ========================================================
          TITLE
      ======================================================== */}

      <div
        role="columnheader"
        className="
          flex
          min-w-0
          items-center
          pl-0.5
        "
      >
        <span
          className="
            truncate
            text-[.62rem]
            font-medium
            uppercase
            tracking-[.08em]
          "
        >
          Título
        </span>
      </div>

      {/* ========================================================
          OFFLINE
      ======================================================== */}

      <div
        role="columnheader"
        aria-label="Disponible offline"
        className="
          flex
          items-center
          justify-center
        "
      >
        <CircleArrowDown
          size={15}
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* ========================================================
          FAVORITE
      ======================================================== */}

      <div
        role="columnheader"
        aria-label="Favoritos"
        className="
          flex
          items-center
          justify-center
        "
      >
        <Heart
          size={15}
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* ========================================================
          MORE
      ======================================================== */}

      <div
        role="columnheader"
        aria-label="Más opciones"
        className="
          flex
          items-center
          justify-center
        "
      >
        <MoreHorizontal
          size={16}
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* ========================================================
          DURATION
      ======================================================== */}

      <div
        role="columnheader"
        className="
          flex
          items-center
          justify-center
        "
      >
        <span
          className="
            text-[.62rem]
            font-medium
            uppercase
            tracking-[.08em]
          "
        >
          Time
        </span>
      </div>
    </div>
  )
}

/* ================================================================
   PLAYLIST TRACK
================================================================ */

function playlistTracklist({
  data,
}: playlistTracklistProps) {
  const {
    playSong,
    currentTrackData,
    isPlaying: musicIsPlaying,
  } = useMusicContext()

  const {
    isOfflineSaved,
    isFavorite,
    isSavingOffline,
    toggleFavorite,
    saveOffline,
  } = usePlaylistTrackState({
    song: data.song,
  })

  const [showContextMenu, setShowContextMenu] =
    useState(false)

  const [showModal, setShowModal] =
    useState(false)

  /* ==============================================================
     CURRENT TRACK

     Derivado directamente del contexto.
     No useEffect.
     No setState.
     ============================================================= */

  const isThisSong =
    currentTrackData?.track === data.song.link &&
    currentTrackData?.type !== "radio" &&
    musicIsPlaying

  /* ==============================================================
     PLAY
     ============================================================= */

  const handlePlay = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault()
      event.stopPropagation()

      const index = Math.max(
        0,
        Number(data.song.index) - 1
      )

      playSong(
        data.song,
        index,
        data.playlist
      )
    },
    [
      data.song,
      data.playlist,
      playSong,
    ]
  )

  /* ==============================================================
     CONTEXT MENU
     ============================================================= */

  const openTrackDrawer = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault()
      event.stopPropagation()

      setShowContextMenu(true)
    },
    []
  )

  const closeContextMenu = useCallback(() => {
    setShowContextMenu(false)
  }, [])

  /* ==============================================================
     OFFLINE
     ============================================================= */

  const openOfflineModal = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (isOfflineSaved) return

      setShowModal(true)
    },
    [isOfflineSaved]
  )

  const confirmSaveOffline = useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (isSavingOffline) return

      const saved =
        await saveOffline()

      if (saved) {
        setShowModal(false)
      }
    },
    [
      isSavingOffline,
      saveOffline,
    ]
  )

  const cancelOffline = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (isSavingOffline) return

      setShowModal(false)
    },
    [isSavingOffline]
  )

  /* ==============================================================
     FAVORITE
     ============================================================= */

  const handleFavorite = useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault()
      event.stopPropagation()

      await toggleFavorite()
    },
    [toggleFavorite]
  )

  /* ==============================================================
     CONTEXT ACTIONS
     ============================================================= */

  const handleContextPlay = useCallback(() => {
    setShowContextMenu(false)
  }, [])

  const handleContextArtist = useCallback(() => {
    setShowContextMenu(false)

    // navigate(...)
  }, [])

  const handleContextPlaylist = useCallback(() => {
    setShowContextMenu(false)

    // navigate(...)
  }, [])

  const handleContextFavorite =
    useCallback(async () => {
      await toggleFavorite()
      setShowContextMenu(false)
    }, [toggleFavorite])

  const handleContextOffline =
    useCallback(() => {
      if (
        isOfflineSaved ||
        isSavingOffline
      ) {
        return
      }

      setShowContextMenu(false)
      setShowModal(true)
    }, [
      isOfflineSaved,
      isSavingOffline,
    ])

  /* ==============================================================
     RENDER
     ============================================================== */

  return (
    <>
      {/* ==========================================================
          TRACK
      ========================================================== */}

      <div
        role="row"
        className={`
          group
          relative
          grid
          h-15
          w-full
          ${TRACK_GRID_MOBILE}
          items-center
          gap-2
          rounded-lg
          px-2
          select-none
          transition-colors
          duration-150
          hover:bg-white/[.035]
          active:bg-white/[.055]
          lg:${TRACK_GRID}
          lg:gap-3
          lg:px-4
        `}
      >
        {/* ======================================================
            PLAY BUTTON

            El botón ocupa visualmente la zona del índice.
            Solo aparece al interactuar.
        ====================================================== */}

        <button
          type="button"
          aria-label={
            isThisSong
              ? `Pausar ${data.song.songName}`
              : `Reproducir ${data.song.songName}`
          }
          onClick={handlePlay}
          className="
            absolute
            left-0
            z-20
            flex
            h-10
            w-12
            items-center
            justify-center
            rounded-md
            opacity-0
            transition-opacity
            duration-150
            group-hover:opacity-100
            focus-visible:opacity-100
            focus-visible:outline-none
            focus-visible:ring-2
            focus-visible:ring-white/40
          "
        >
          {isThisSong ? (
            <Icons.Pause />
          ) : (
            <Icons.Play />
          )}
        </button>

        {/* ======================================================
            INDEX / PLAYING
        ====================================================== */}

        <div
          className="
            flex
            h-full
            min-w-0
            items-center
            justify-center
            text-[.6em] md:text-[.7em]
            tabular-nums
            text-white/50
          "
        >
          {isThisSong ? (
            <img
              src={Playgif}
              alt="Reproduciendo"
              className="
                h-auto
                max-h-10
                w-[.6rem]
                object-contain
              "
              draggable={false}
            />
          ) : (
            <span>
              {data.song.index}
            </span>
          )}
        </div>

        {/* ======================================================
            COVER
        ====================================================== */}

        {data.listType !== "album" ? (
          <div
            className="
              flex
              h-10
              w-10
              shrink-0
              overflow-hidden
              rounded-[.25rem]
              bg-white/[.045]
              ring-1
              ring-inset
              ring-white/[.06]
            "
          >
            <img
              src={data.song.songimg}
              alt=""
              className="
                h-full
                w-full
                object-cover
              "
              draggable={false}
            />
          </div>
        ) : (
          <div
            aria-hidden="true"
            className="
              h-10
              w-10
              shrink-0
            "
          />
        )}

        {/* ======================================================
            TRACK INFORMATION
        ====================================================== */}

        <div
          className="
            flex
            min-w-0
            flex-col
            justify-center
            overflow-hidden
            leading-none
          "
        >
          <TextBoldL>
            <span
              className="
                block
                truncate text-sm
              "
            >
              {data.song.songName}
            </span>
          </TextBoldL>

          <TextRegularM>
            <span
              className="
                mt-0
                block
                truncate
              "
            >
              {data.song.songArtist}
            </span>
          </TextRegularM>
        </div>

        {/* ======================================================
            OFFLINE
        ====================================================== */}

        <button
          type="button"
          aria-label={
            isOfflineSaved
              ? "Guardado offline"
              : `Guardar ${data.song.songName} offline`
          }
          aria-pressed={isOfflineSaved}
          onClick={openOfflineModal}
          disabled={isOfflineSaved}
          className={`
            z-10
            flex
            h-8
            w-8
            shrink-0
            items-center
            justify-center
            rounded-full
            transition-all
            duration-150
            focus-visible:outline-none
            focus-visible:ring-2
            focus-visible:ring-white/30
            ${
              isOfflineSaved
                ? "cursor-default"
                : "hover:bg-black/40 hover:text-white active:scale-95 active:bg-white/10"
            }
          `}
        >
          <CircleArrowDown
            size={19}
            strokeWidth={2}
            className={`
              transition-colors
              duration-150
              ${
                isOfflineSaved
                  ? "text-blue-500"
                  : "text-white/45"
              }
            `}
          />
        </button>

        {/* ======================================================
            FAVORITE
        ====================================================== */}

        <button
          type="button"
          aria-label={
            isFavorite
              ? `Quitar ${data.song.songName} de favoritos`
              : `Añadir ${data.song.songName} a favoritos`
          }
          aria-pressed={isFavorite}
          onClick={handleFavorite}
          className="
            z-10
            flex
            h-8
            w-8
            shrink-0
            items-center
            justify-center
            rounded-full
            transition-all
            duration-150
            hover:bg-black/40
            active:scale-95
            active:bg-white/10
            focus-visible:outline-none
            focus-visible:ring-2
            focus-visible:ring-white/30
          "
        >
          <Heart
            size={19}
            strokeWidth={2}
            className={`
              transition-all
              duration-150
              ${
                isFavorite
                  ? "fill-red-500 text-red-500"
                  : "text-white/45"
              }
            `}
          />
        </button>

        {/* ======================================================
            MORE
        ====================================================== */}

        <button
          type="button"
          aria-label={`Opciones de ${data.song.songName}`}
          onPointerDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onClick={openTrackDrawer}
          className="
            relative
            z-[50]
            flex
            h-8
            w-8
            shrink-0
            items-center
            justify-center
            rounded-full
            transition-all
            duration-150
            hover:bg-black/40
            active:scale-95
            active:bg-white/10
            focus-visible:outline-none
            focus-visible:ring-2
            focus-visible:ring-white/30
          "
        >
          <MoreHorizontal
            size={20}
            strokeWidth={2}
            className="text-white/45"
          />
        </button>

        {/* ======================================================
            DURATION
        ====================================================== */}

        <p
          className="
            flex
            min-w-0
            items-center
            justify-center
            whitespace-nowrap
            text-xs
            tabular-nums
            text-white/45
          "
        >
          {data.song.trackTime}
        </p>
      </div>

      {/* ==========================================================
          CONTEXT MENU
      ========================================================== */}

      {showContextMenu && (
        <TrackContextMenu
          open={showContextMenu}
          song={data.song}
          isFavorite={isFavorite}
          isOfflineSaved={isOfflineSaved}
          isSavingOffline={isSavingOffline}
          onPlay={handleContextPlay}
          onArtist={handleContextArtist}
          onPlaylist={handleContextPlaylist}
          onFavorite={handleContextFavorite}
          onOffline={handleContextOffline}
          onClose={closeContextMenu}
        />
      )}

      {/* ==========================================================
          OFFLINE MODAL
      ========================================================== */}

      {showModal && (
        <div
          className="
            fixed
            inset-0
            z-[10000]
            flex
            items-center
            justify-center
            bg-black/70
            p-4
            backdrop-blur-sm
          "
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="offline-dialog-title"
            className="
              w-full
              max-w-[18rem]
              overflow-hidden
              rounded-2xl
              border
              border-white/10
              bg-black
              p-4
              text-center
              shadow-2xl
            "
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            <p
              id="offline-dialog-title"
              className="
                text-sm
                font-semibold
                text-white
              "
            >
              ¿Guardar en Offline?
            </p>

            <p
              className="
                mt-1
                text-xs
                leading-relaxed
                text-white/40
              "
            >
              La reproducción continuará sin interrupciones.
            </p>

            <div
              className="
                mt-4
                grid
                grid-cols-2
                gap-2
              "
            >
              <button
                type="button"
                onClick={confirmSaveOffline}
                disabled={isSavingOffline}
                className="
                  rounded-md
                  bg-[#007aff]
                  px-4
                  py-2
                  text-sm
                  font-semibold
                  text-white
                  transition-all
                  duration-150
                  hover:bg-[#2c92ff]
                  active:scale-[.98]
                  disabled:pointer-events-none
                  disabled:opacity-50
                "
              >
                {isSavingOffline
                  ? "Guardando..."
                  : "Guardar"}
              </button>

              <button
                type="button"
                onClick={cancelOffline}
                disabled={isSavingOffline}
                className="
                  rounded-md
                  bg-white/20
                  px-4
                  py-2
                  text-sm
                  font-semibold
                  text-white
                  transition-all
                  duration-150
                  hover:bg-white/30
                  active:scale-[.98]
                  disabled:pointer-events-none
                  disabled:opacity-50
                "
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default memo(playlistTracklist)